/**
 * CopyLinkIcon — small link/share icon that copies a URL to the
 * clipboard with a tooltip on hover. Designed to overlay the corner
 * of a photo/avatar/card.
 *
 * Props:
 *   url            (string, required) — URL to copy
 *   position       ("tl"|"tr"|"bl"|"br") — corner. default "tr".
 *   label          (string) — hover label. default "Copy Link".
 *   testId         (string) — data-testid.
 *   size           ("sm"|"md") — default "sm".
 *
 * Behaviour:
 *   Click → copies the absolute URL to the clipboard, shows a small
 *   "Copied!" toast + swaps the icon to a checkmark for 1.5s.
 */
import { useState, useCallback } from "react";
import { Link2, Check } from "lucide-react";
import { toast } from "sonner";

const POS = {
  tl: "top-2 left-2",
  tr: "top-2 right-2",
  bl: "bottom-2 left-2",
  br: "bottom-2 right-2",
};

const SIZE = {
  sm: { btn: "w-7 h-7", icon: "w-3.5 h-3.5" },
  md: { btn: "w-9 h-9", icon: "w-4 h-4" },
};

export default function CopyLinkIcon({
  url,
  position = "tr",
  label = "Copy Link",
  testId,
  size = "sm",
  onCopied,
}) {
  const [copied, setCopied] = useState(false);
  const sz = SIZE[size] || SIZE.sm;

  const copy = useCallback((e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    // React Router <Link> and SVG <a> can still capture bubbling — kill it at
    // the native layer so clicking the icon never triggers navigation.
    e?.nativeEvent?.stopImmediatePropagation?.();
    if (!url) return;
    // Resolve to absolute so pasted links work outside the app
    const abs = /^https?:\/\//i.test(url) ? url : `${window.location.origin}${url}`;
    const done = () => {
      setCopied(true);
      toast.success("Link copied to clipboard");
      onCopied?.(abs);
      setTimeout(() => setCopied(false), 1600);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(abs).then(done).catch(() => {
        // Fallback: transient textarea copy
        const t = document.createElement("textarea");
        t.value = abs; document.body.appendChild(t); t.select();
        try { document.execCommand("copy"); done(); }
        catch { toast.error("Couldn't copy — copy manually: " + abs); }
        finally { document.body.removeChild(t); }
      });
    } else {
      toast.error("Clipboard not supported in this browser");
    }
  }, [url, onCopied]);

  return (
    <button
      type="button"
      onClick={copy}
      title={copied ? "Copied!" : label}
      aria-label={label}
      className={`absolute ${POS[position]} ${sz.btn} rounded-full bg-white/85 hover:bg-white shadow-md flex items-center justify-center backdrop-blur-sm transition-transform hover:scale-110 z-10 group`}
      style={{ color: copied ? "#059669" : "#1F2937" }}
      data-testid={testId || "copy-link-icon"}
    >
      {copied ? <Check className={sz.icon} /> : <Link2 className={sz.icon} />}
      <span
        className="absolute right-full mr-1 top-1/2 -translate-y-1/2 whitespace-nowrap text-[10px] font-medium bg-slate-900 text-white px-2 py-0.5 rounded opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity"
      >
        {copied ? "Copied!" : label}
      </span>
    </button>
  );
}
